import type { PropertyFileResponse } from '@/shared/api';
import type { ActionMenuItem } from '@/shared/components';
import { formatDate } from '@/shared/lib/format';

/** Row-/Detail-Actions einer Datei. Move/Reclassify sind optional (in C1 noch nicht verdrahtet). */
export interface FileActions {
  onPreview: (f: PropertyFileResponse) => void;
  onDownload: (f: PropertyFileResponse) => void;
  onEdit: (f: PropertyFileResponse) => void;
  onDelete: (f: PropertyFileResponse) => void;
  onMove?: (f: PropertyFileResponse) => void;
  onReclassify?: (f: PropertyFileResponse) => void;
  /** Direkter Move (Drag-&-Drop) in einen Ziel-Ordner derselben Kategorie. */
  onMoveTo?: (f: PropertyFileResponse, targetFolder: string) => void;
}

/** Tooltip für deaktivierte Aktionen an gesperrten Belegen (§4.10). */
export function lockedTooltip(file: PropertyFileResponse): string {
  const until = file.retentionUntil ? formatDate(file.retentionUntil) : 'unbefristet';
  return `Gesperrter Beleg (GoBD). Nicht veränderbar. Aufbewahrung bis ${until}.`;
}

/**
 * Baut die Row-Menü-Einträge einer Datei. Bei `locked` sind mutierende Aktionen
 * (Bearbeiten/Verschieben/Kategorie ändern/Löschen) deaktiviert + Tooltip erklärt das Warum.
 */
export function buildFileMenuItems(file: PropertyFileResponse, a: FileActions): ActionMenuItem[] {
  const locked = file.locked;
  const title = locked ? lockedTooltip(file) : undefined;
  const items: ActionMenuItem[] = [
    { label: 'Vorschau', icon: 'Eye', onClick: () => a.onPreview(file) },
    { label: 'Herunterladen', icon: 'Download', onClick: () => a.onDownload(file) },
    { label: 'Bearbeiten', icon: 'Edit', onClick: () => a.onEdit(file), disabled: locked, title },
  ];
  if (a.onMove) {
    items.push({
      label: 'Verschieben nach…',
      icon: 'Arrow',
      onClick: () => a.onMove!(file),
      disabled: locked,
      title,
    });
  }
  if (a.onReclassify) {
    items.push({
      label: 'Kategorie ändern…',
      icon: 'Refresh',
      onClick: () => a.onReclassify!(file),
      disabled: locked,
      title,
    });
  }
  items.push({ separator: true });
  items.push({
    label: 'Löschen',
    icon: 'Trash',
    onClick: () => a.onDelete(file),
    destructive: true,
    disabled: locked,
    title,
  });
  return items;
}
